import React, { Suspense, lazy } from 'react';
import qs from 'qs';
import { useTranslate } from 'hooks/useTranslate';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import styled from '@emotion/styled';
import { getProfessionInfo, getVacancies } from 'actions/profession';
import { getCompanySponsorship } from 'actions/company';
import { toggleFavorite } from 'actions/profession';

import { triggerDailyQuest } from 'actions/gamification';
import { isAuthenticated } from 'helpers/authToken';
import getCurrentId from 'helpers/getCurrentId';
import awaitDelay from 'helpers/awaitDelay';
import { ADSENSE_ACTIVE, IS_SNAP, IS_PRERENDER, DAILY } from 'variables/main';
import Preloader from 'components/Preloader';
import Helmet from 'components/Helmet';
import MainLayout from 'layouts/MainLayout';
import { generateOccupationSchema, getBaseUrl, buildEntityUrl, getProfessionSlug } from 'utils/seo';
import LazyComponent from 'components/LazyComponent';
import { getLocalizedPath } from 'helpers/getLocale';
import AdSense from 'components/AdSense';
import BG_COLORS from './helpers/index';
import { getSimulator } from 'pages/Simulator/catalog';
import professionCases from 'helpers/professionCases';
const NeedAuthDialog = lazy(() => import('components/NeedAuthDialog'));
const Header = lazy(() => import('components/Header'));
const Footer = lazy(() => import('components/Footer'));
const QuestionsComponent = lazy(() => import('components/Questions'));
const LinearProgress = lazy(() => import('components/LinearProgress'));
const ShortInfoComponent = lazy(() => import('./components/ShortInfo'));
const SkillsComponent = lazy(() => import('./components/Skills'));
const ResponsibilitiesComponent = lazy(() => import('./components/Responsibilities'));
const ScheduleComponent = lazy(() => import('./components/Schedule'));
const BenefitsComponent = lazy(() => import('./components/Benefits'));
const VacanciesComponent = lazy(() => import('./components/Vacancies'));
const StepsToSwitch = lazy(() => import('./components/StepsToSwitch'));
const GetConsultationComponent = lazy(() => import('./components/GetConsultation'));
const SimilarProfessionsComponent = lazy(() => import('./components/SimilarProfessions'));
const InstitutionsComponent = lazy(() => import('./components/Institutions'));
const DescriptionComponent = lazy(() => import('./components/Description'));
const AiRiskComponent = lazy(() => import('./components/AiRisk'));
const SimulatorBanner = lazy(() => import('./components/SimulatorBanner'));
const PageNavigation = lazy(() => import('./components/PageNavigation'));
const VoteComponent = lazy(() => import('./components/Vote'));
const Sponsorship = lazy(() => import('./components/Sponsorship'));
const Slides = lazy(() => import('./layout/Slides'));

const HEADER_HEIGHT = 108;
const WRAPPER_WIDTH = 1440;
const BANNERS_WIDTH = window.innerWidth < 1600;

const FixedWrapper = styled('div')(({ theme }) => ({
  padding: '45px 0px',
  paddingBottom: 0,
  ...theme.wrapper,
  maxWidth: 1240,
  [theme.breakpoints.down('lg')]: {
    padding: '45px 20px'
  },
  [theme.breakpoints.down('md')]: {
    padding: 20,
    paddingTop: 0
  }
}));

const LeftBanner = styled('div')(({ top, left, isNaN }) => ({
  position: 'fixed',
  top,
  left,
  height: '100%',
  transition: 'top 0.1s ease-out',
  opacity: isNaN === true ? 0 : 1,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}));

const RightBanner = styled(LeftBanner)(({ right }) => ({
  left: 'unset',
  right
}));

const FlexCards = styled('section')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 20,
  marginBottom: 25,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: 0
  }
}));

const RenderBanner = ({ src, scrolled, left = false }) => {
  const ref = React.useRef(null);
  const [dimensions, setDimensions] = React.useState(null);

  React.useEffect(() => {
    if (!ref.current) return;

    const fetchDimensions = () => {
      const width = ref.current.offsetWidth;
      const height = ref.current.offsetHeight;

      if (!width || !height) return;

      if (!dimensions) {
        setDimensions({ width, height });
      }
    };

    fetchDimensions();

    setTimeout(() => {
      fetchDimensions();
    }, 250);
  }, [ref, dimensions]);

  const position = React.useMemo(
    () => (window.innerWidth - WRAPPER_WIDTH) / 2 - dimensions?.width - 2,
    [dimensions]
  );

  const isNaN = Number.isNaN(position);

  if (BANNERS_WIDTH || IS_SNAP) return null;

  if (left) {
    return (
      <LeftBanner ref={ref} top={!scrolled ? HEADER_HEIGHT : 0} left={position} isNaN={isNaN}>
        <img alt="" src={src} />
      </LeftBanner>
    );
  }

  return (
    <RightBanner ref={ref} top={!scrolled ? HEADER_HEIGHT : 0} right={position} isNaN={isNaN}>
      <img alt="" src={src} />
    </RightBanner>
  );
};

const Profession = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slide, slug } = useParams();

  const [currentId, setCurrentId] = React.useState(getCurrentId(location));
  const [professionInfo, setProfessionInfo] = React.useState(null);
  const [vacanciesList, setVacanciesList] = React.useState(null);
  const [expendedSkills, setExpendedSkills] = React.useState(false);
  const [isFavorite, setIsFavorite] = React.useState(false);
  const [needAuth, setNeedAuth] = React.useState(false);
  const [sponsor, setSponsor] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [headerScrolled, setHeaderScrolled] = React.useState(false);
  const [isBlockVisible, setIsBlockVisible] = React.useState({});
  const [highlight, setHighlight] = React.useState(false);
  const [viewer, setViewer] = React.useState(!!slide);

  // Update viewer state when slide parameter changes
  React.useEffect(() => {
    setViewer(!!slide);
  }, [slide]);

  // Handle slide URL redirection
  React.useEffect(() => {
    if (professionInfo && slide === 'overview') {
      // If someone visits the overview slide, we're already in the right place
      return;
    }
  }, [professionInfo, slide]);

  const t = useTranslate('Profession');
  const tHelmet = useTranslate('HELMET');

  const profInfo = useSelector((state) => state.profession.state[currentId]);
  const vacanciesInfo = useSelector((state) => state.profession.vacancies[currentId]);

  const handleSetVisible = React.useCallback((block) => {
    setIsBlockVisible((prev) => ({
      ...prev,
      [block]: true
    }));
  }, []);

  const handleClose = React.useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setNeedAuth(false);
  }, []);

  const handleGetConsultation = React.useCallback(() => {
    navigate(getLocalizedPath('/counselors'));
  }, [navigate]);

  const handleChooseTag = React.useCallback(
    (tag) => {
      navigate(getLocalizedPath(`/professions?tag_ids=${tag}`));
    },
    [navigate]
  );

  const handleToggleFavorite = React.useCallback(
    (e) => {
      e.stopPropagation();

      if (!isAuthenticated()) {
        setNeedAuth(true);
        return;
      }

      dispatch(toggleFavorite(currentId, isFavorite, 'professions'));
      setIsFavorite(!isFavorite);
    },
    [dispatch, currentId, isFavorite]
  );

  const fullPage = React.useCallback(() => {
    setIsBlockVisible({
      scrollItem_1: true,
      scrollItem_2: true,
      scrollItem_3: true,
      scrollItem_4: true,
      scrollItem_5: true,
      scrollItem_6: true,
      scrollItem_7: true,
      scrollItem_8: true,
      scrollItem_9: true,
      scrollItem_10: true,
      scrollItem_11: true
    });
  }, []);

  const fetchVacanciesData = React.useCallback(
    async (id) => {
      // The vacancies block isn't rendered into the crawler snapshot, so don't
      // pay for its request either — this is one of the slowest calls on the
      // page and the prerender waits for the network to settle before capturing.
      if (IS_PRERENDER) {
        setVacanciesList([]);
        return;
      }
      try {
        const vacanciesList = await dispatch(getVacancies(id));
        setVacanciesList(Array.isArray(vacanciesList) ? vacanciesList : []);
      } catch {
        // Missing/404 vacancies sub-resource: render an empty list, don't crash.
        setVacanciesList([]);
      }
    },
    [dispatch]
  );

  const fetchSponsors = React.useCallback(async () => {
    const urlData = {
      offset: 0,
      limit: 1,
      fields: ['_id', 'description', 'name', 'leftbanner', 'rightbanner']
    };
    const queryString = qs.stringify(urlData);
    setLoading(true);
    try {
      const result = await dispatch(getCompanySponsorship(professionInfo._id, `?${queryString}`));
      setSponsor(result && !(result instanceof Error) ? result : null);
    } catch {
      // Missing/404 sponsor: just render without one.
      setSponsor(null);
    } finally {
      setLoading(false);
    }
  }, [dispatch, professionInfo]);

  const handleHighlight = React.useCallback((child) => {
    setTimeout(() => {
      setHighlight(child);
      setTimeout(() => {
        setHighlight(false);
      }, 2500);
    }, 500);
  }, []);

  const scrollToBlock = React.useCallback(
    async (index) => {
      const scrollToElement = async () => {
        const active = document.getElementById(`scrollItem_${index}`);

        if (active) {
          active.scrollIntoView({ behavior: 'smooth' });
          handleHighlight(`scrollItem_${index}`);
          return;
        }

        let retries = 10;
        const interval = setInterval(async () => {
          const active = document.getElementById(`scrollItem_${index}`);
          if (active) {
            active.scrollIntoView({ behavior: 'smooth' });
            handleHighlight(`scrollItem_${index}`);
            await awaitDelay(250);
            clearInterval(interval);
          } else if (--retries === 0) {
            clearInterval(interval);
          }
        }, 100);
      };

      scrollToElement();
    },
    [handleHighlight]
  );

  const handleChangeView = React.useCallback(() => {
    setViewer((prev) => !prev);

    // Leaving slides view → return to the main profession URL. Use route
    // params (and getLocalizedPath) so this works under the localized
    // `/:lc/*` layout — the previous form parsed pathname segments by
    // index and produced `/professions/professions/<id>` on jobalto.com.
    if (viewer && slide && slug && currentId) {
      navigate(getLocalizedPath(`/professions/${slug}/${currentId}`));
    }
  }, [viewer, slide, slug, currentId, navigate]);

  React.useEffect(() => {
    if (professionInfo?._id) fetchSponsors();
  }, [fetchSponsors, professionInfo]);

  React.useEffect(() => {
    const satPageData = (obj) => {
      if (!obj) return;
      setProfessionInfo(obj);
      const isFavorite = obj?.isFavorite;
      setIsFavorite(isFavorite);
    };

    const fetchData = async () => {
      const resultInfo = await dispatch(getProfessionInfo(currentId));
      if (!resultInfo || resultInfo instanceof Error) {
        navigate('/');
        return;
      }

      // Redirect /professions/undefined/:id to correct URL (fixes Google-indexed broken URLs)
      if (slug === 'undefined') {
        const correctSlug = getProfessionSlug(resultInfo);
        const correctUrl = getLocalizedPath(`/professions/${correctSlug}/${currentId}`);
        window.location.replace(correctUrl);
        return;
      }

      // Check if this is a synonym and redirect to canonical
      if (resultInfo.synonymOf) {
        const canonicalId = resultInfo.synonymOf;
        // Fetch canonical profession to get its name for slug generation
        const canonicalProfession = await dispatch(getProfessionInfo(canonicalId));
        if (canonicalProfession && canonicalProfession._id) {
          // getProfessionSlug picks the slug for the current locale (locale
          // map → name${LANG} sibling → bare `name`), so the redirect lands
          // on the locale-correct URL.
          const canonicalSlug = getProfessionSlug(canonicalProfession) || slug;
          const canonicalUrl = getLocalizedPath(`/professions/${canonicalSlug}/${canonicalId}`);
          // Use replace for 301-like permanent redirect (better for SEO)
          window.location.replace(canonicalUrl);
          return;
        }
      }

      // Stale-slug redirect. If a user lands via an old or non-locale slug
      // (e.g. /de-de/professions/perekladach/<id> after we shipped
      // /de-de/professions/uebersetzer/<id>), bounce them to the canonical
      // locale-correct URL so Google folds them together.
      const expectedSlug = getProfessionSlug(resultInfo);
      if (expectedSlug && slug && expectedSlug !== slug) {
        const canonicalUrl = getLocalizedPath(`/professions/${expectedSlug}/${currentId}`);
        window.location.replace(canonicalUrl);
        return;
      }

      // If resultInfo indicates this was accessed via synonym, add canonical tag
      if (resultInfo.isSynonym && resultInfo.synonymId) {
        // The canonical URL is the current URL (since we're showing canonical content)
        // This will be handled in Helmet component via canonicalUrl prop
      }

      await satPageData(resultInfo);
      await fetchVacanciesData(currentId);
    };

    if (!currentId) {
      navigate('/');
      return;
    }

    if (profInfo) {
      satPageData(profInfo);
      setVacanciesList(vacanciesInfo);
      return;
    }

    // .catch guards against any rejection from the canonical re-fetch path
    // becoming an unhandled rejection (Sentry PROFITROLL-FRONTEND-B).
    fetchData().catch(() => {});
  }, [dispatch, navigate, currentId, profInfo, fetchVacanciesData, vacanciesInfo, slug]);

  React.useEffect(() => {
    const newId = getCurrentId(location);

    // Always expand all sections — including during prerender (IS_SNAP) so
    // SimilarProfessions and Footer render as real <a> tags, giving each
    // profession page outgoing links and fixing Ahrefs "no outgoing links".
    fullPage();

    if (newId !== currentId) {
      setCurrentId(newId);
    }
  }, [location, currentId, fullPage]);

  React.useEffect(() => {
    let timeout;

    if (isBlockVisible.scrollItem_11) {
      timeout = setTimeout(() => {
        dispatch(triggerDailyQuest(DAILY.readProfession));
      }, 10000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [dispatch, isBlockVisible]);

  React.useEffect(() => {
    const listenScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY >= HEADER_HEIGHT) {
        setHeaderScrolled(true);
      } else {
        setHeaderScrolled(false);
      }
    };

    window.addEventListener('scroll', listenScroll);

    return () => {
      window.removeEventListener('scroll', listenScroll);
    };
  }, []);

  const breadcrumbs = React.useMemo(() => {
    return [
      {
        label: 'Home',
        link: '/'
      },
      {
        label: 'Professions',
        link: '/professions'
      },
      {
        label: professionInfo ? professionInfo?.name : '',
        raw: true
      }
    ];
  }, [professionInfo]);

  const [color, setColor] = React.useState(BG_COLORS[0]);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const percentage = scrollTop / maxScroll;

      const colorIndex = Math.floor(percentage * (BG_COLORS.length - 1));
      setColor(BG_COLORS[colorIndex]);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Slide variants that are real, indexable surfaces with their own title /
  // description / canonical. Keep in sync with `slideNames` in
  // `pages/Profession/layout/Slides.jsx`. The Pascal-case value picks the
  // matching HELMET translation key (`professionSlide{Pascal}Title`/`Description`).
  const PROFESSION_SLIDE_META_KEYS = React.useMemo(
    () => ({
      overview: 'Overview',
      description: 'Description',
      skills: 'Skills',
      schedule: 'Schedule',
      responsibilities: 'Responsibilities',
      benefits: 'Benefits',
      questions: 'Questions',
      steps: 'Steps',
      education: 'Education',
      vacancies: 'Vacancies',
      qa: 'Qa',
      similar: 'Similar',
      'ai-risk': 'AiRisk'
    }),
    []
  );
  const slideMetaKey = slide ? PROFESSION_SLIDE_META_KEYS[slide] : null;
  const currentYearForMeta = React.useMemo(() => new Date().getFullYear(), []);

  // Slide-specific title/description so each slide URL is its own indexable
  // page (the sitemap now lists them — see scripts/sitemap.js).
  const slideMeta = React.useMemo(() => {
    if (!professionInfo || !slideMetaKey) return null;
    const tplVars = {
      ...professionCases(professionInfo),
      name: professionInfo?.name || '',
      currentYear: currentYearForMeta
    };
    return {
      title: tHelmet(`professionSlide${slideMetaKey}Title`, tplVars),
      description: tHelmet(`professionSlide${slideMetaKey}Description`, tplVars)
    };
  }, [professionInfo, slideMetaKey, currentYearForMeta, tHelmet]);

  // Canonical URL — always the base profession URL, even when the user is on
  // a slide variant (/professions/:slug/:id/:slide). The slide URLs are
  // present in the sitemap so crawlers can discover them, but they all
  // consolidate ranking signals to the canonical profession page. Each slide
  // still ships a distinct title/description for browser tabs and social
  // shares, but Google treats the slug+id URL as the indexable surface.
  const canonicalUrl = React.useMemo(() => {
    if (!professionInfo) return undefined;
    const slug = getProfessionSlug(professionInfo);
    return buildEntityUrl('profession', slug, professionInfo._id);
  }, [professionInfo]);

  // Generate structured data for the profession
  const baseUrl = React.useMemo(() => getBaseUrl(), []);

  // Generate AMP URL - only for non-localized pages (profitroll.com.ua)
  // AMP pages exist only for Ukrainian version
  const ampUrl = React.useMemo(() => {
    if (!professionInfo) return undefined;

    // Check if we're on a localized path or already on AMP page
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      // Don't add amphtml link on localized paths (jobalto.com)
      if (/^\/[a-z]{2}-[a-z]{2}(\/|$)/i.test(path)) {
        return undefined;
      }
      // Don't add amphtml link if we're already on an AMP page
      if (path.startsWith('/amp/')) {
        return undefined;
      }
    }

    // Build AMP URL for profitroll.com.ua — always the Ukrainian slug since
    // profitroll.com.ua is the Ukrainian property. Explicit 'uk' so this stays
    // correct even when the backend ships locale-keyed `name`.
    const slug = getProfessionSlug(professionInfo, 'uk');
    return `https://profitroll.com.ua/amp/${slug}/${professionInfo._id}`;
  }, [professionInfo]);

  const simulator = React.useMemo(() => getSimulator(professionInfo?._id), [professionInfo]);

  const professionSchema = React.useMemo(() => {
    if (!professionInfo) return null;
    return generateOccupationSchema(professionInfo, baseUrl);
  }, [professionInfo, baseUrl]);

  if (!professionInfo) {
    return <Preloader />;
  }

  return (
    <>
      <Helmet
        page={'profession'}
        name={professionInfo?.name || ''}
        declensions={professionInfo?.declensions}
        meta={slideMeta || undefined}
        metaImageUrl={
          professionInfo?.photoLinks?.main ? `${professionInfo?.photoLinks?.main}/w1200h627` : null
        }
        canonicalUrl={canonicalUrl}
        ampUrl={ampUrl}
        schema={professionSchema}
        breadcrumbs={breadcrumbs}
        ogType="article"
        includeHreflang={true}
      />
      <MainLayout
        styles={{
          backgroundColor: theme.palette.backgroundLight.main,
          background: viewer ? theme.palette.backgroundLight.main : color,
          transition: 'background 0.2s ease-out'
        }}>
        {viewer ? (
          <>
            <Slides
              professionInfo={professionInfo}
              highlight={highlight}
              handleChangeView={handleChangeView}
              t={t}
              isFavorite={isFavorite}
              handleToggleFavorite={handleToggleFavorite}
              handleChooseTag={handleChooseTag}
              breadcrumbs={breadcrumbs}
              sponsor={sponsor}
              loading={loading}
              handleSetVisible={handleSetVisible}
              vacanciesList={vacanciesList}
              handleGetConsultation={handleGetConsultation}
              slug={professionInfo?.slug}
              id={professionInfo?._id}
            />
          </>
        ) : (
          <>
            <Header />

            <FixedWrapper>
              {sponsor?.leftbanner ? (
                <RenderBanner
                  left={true}
                  scrolled={headerScrolled}
                  src={`${sponsor?.leftbanner}/public`}
                />
              ) : null}

              {sponsor?.rightbanner ? (
                <RenderBanner scrolled={headerScrolled} src={`${sponsor?.rightbanner}/public`} />
              ) : null}

              <Sponsorship sponsor={sponsor} professionInfo={professionInfo} />

              <Suspense fallback={<LinearProgress />}>
                <LazyComponent
                  id={'scrollItem_0'}
                  component={ShortInfoComponent}
                  professionInfo={professionInfo}
                  handleToggleFavorite={handleToggleFavorite}
                  isFavorite={isFavorite}
                  t={t}
                  handleChooseTag={handleChooseTag}
                  breadcrumbs={breadcrumbs}
                  sponsor={sponsor}
                  loading={loading}
                  highlight={highlight}
                />
              </Suspense>

              <Suspense fallback={<LinearProgress />}>
                <LazyComponent
                  key={professionInfo._id}
                  component={PageNavigation}
                  handleClick={scrollToBlock}
                  professionInfo={professionInfo}
                  highlight={highlight}
                  handleChangeView={handleChangeView}
                />
              </Suspense>

              <Suspense fallback={<LinearProgress />}>
                <LazyComponent
                  id={'scrollItem_1'}
                  component={DescriptionComponent}
                  professionInfo={professionInfo}
                  t={t}
                  setIsVisible={handleSetVisible}
                  highlight={highlight}
                />
              </Suspense>

              {isBlockVisible.scrollItem_1 && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_5_ai'}
                    component={AiRiskComponent}
                    professionInfo={professionInfo}
                    t={t}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              {isBlockVisible.scrollItem_1 && simulator && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_5_sim'}
                    component={SimulatorBanner}
                    simulator={simulator}
                    professionInfo={professionInfo}
                    t={t}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              {isBlockVisible.scrollItem_1 && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_2'}
                    component={(props) => {
                      return (
                        <FlexCards>
                          <SkillsComponent {...props} />
                          <ScheduleComponent {...props} />
                        </FlexCards>
                      );
                    }}
                    t={t}
                    professionInfo={professionInfo}
                    expendedSkills={expendedSkills}
                    setExpendedSkills={setExpendedSkills}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              {isBlockVisible.scrollItem_2 && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_3'}
                    component={ResponsibilitiesComponent}
                    professionInfo={professionInfo}
                    t={t}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              {isBlockVisible.scrollItem_3 && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_4'}
                    component={GetConsultationComponent}
                    handleGetConsultation={handleGetConsultation}
                    setIsVisible={handleSetVisible}
                    t={t}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              {isBlockVisible.scrollItem_4 && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_5'}
                    component={(props) => {
                      return (
                        <FlexCards>
                          <BenefitsComponent {...props} />
                        </FlexCards>
                      );
                    }}
                    professionInfo={professionInfo}
                    t={t}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              <Sponsorship sponsor={sponsor} professionInfo={professionInfo} />

              {isBlockVisible.scrollItem_5 && !IS_PRERENDER && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_6'}
                    key={currentId}
                    component={QuestionsComponent}
                    entity={'reviews'}
                    type={'advices'}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              {isBlockVisible.scrollItem_6 && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_7'}
                    component={StepsToSwitch}
                    professionInfo={professionInfo}
                    t={t}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              {isBlockVisible.scrollItem_7 && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_8'}
                    component={InstitutionsComponent}
                    professionInfo={professionInfo}
                    t={t}
                    key={currentId}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              <Sponsorship sponsor={sponsor} professionInfo={professionInfo} />

              {isBlockVisible.scrollItem_8 && !IS_PRERENDER && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_9'}
                    component={VacanciesComponent}
                    vacanciesList={vacanciesList}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                    title={professionInfo?.name}
                  />
                </Suspense>
              )}

              {isBlockVisible.scrollItem_9 && !IS_PRERENDER && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    id={'scrollItem_10'}
                    key={currentId}
                    component={QuestionsComponent}
                    entity={'questions'}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}

              <Suspense fallback={<LinearProgress />}>
                <LazyComponent component={VoteComponent} />
              </Suspense>

              <Sponsorship sponsor={sponsor} professionInfo={professionInfo} />

              {isBlockVisible.scrollItem_10 && (
                <Suspense fallback={<LinearProgress />}>
                  <LazyComponent
                    component={SimilarProfessionsComponent}
                    professionInfo={professionInfo}
                    key={currentId}
                    id={'scrollItem_11'}
                    t={t}
                    setIsVisible={handleSetVisible}
                    highlight={highlight}
                  />
                </Suspense>
              )}
            </FixedWrapper>

            {isBlockVisible.scrollItem_11 && (
              <Suspense fallback={<LinearProgress />}>
                <LazyComponent component={Footer} setIsVisible={handleSetVisible} />
              </Suspense>
            )}
          </>
        )}

        <NeedAuthDialog handleClose={handleClose} needAuth={needAuth} />

        {ADSENSE_ACTIVE && (
          <AdSense
            adSlot="23272957130"
            adLayout="in-article"
            style={{ display: 'block', textAlign: 'center' }}
          />
        )}
      </MainLayout>
    </>
  );
};

export default Profession;
